import React, { createContext, useContext, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TestProcedure } from "../models/testProcedure.ts";

interface TestProcedureContextType {
  procedures: TestProcedure[];
  addProcedure: (name: string) => string;
  removeProcedure: (id: string) => void;
  updateProcedure: (id: string, updated: TestProcedure) => void;
  setProcedures: (procedures: TestProcedure[]) => void;
}

const TestProcedureContext = createContext<TestProcedureContextType | undefined>(undefined);

export const useTestProcedure = () => {
  const context = useContext(TestProcedureContext);
  if (!context) {
    throw new Error("useTestProcedure must be used within a TestProcedureProvider");
  }
  return context;
};

export const TestProcedureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [procedures, setProcedures] = useState<TestProcedure[]>([]);

  const addProcedure = (name: string) => {
    const newProcedure: TestProcedure = {
      id: uuidv4(),
      name: name,
      serial_number_format: "",
      steps: [],
    };
    setProcedures((prev) => [...prev, newProcedure]);
    return newProcedure.id;
  };

  const removeProcedure = (id: string) => {
    const updated = procedures.filter(item => item.id !== id);
    setProcedures(updated);
  };

  const updateProcedure = (id: string, updated: TestProcedure) => {
    const index = procedures.findIndex(p => p.id === id);
    if (index !== -1) {
      const updatedList = [...procedures];
      updatedList[index] = updated;
      setProcedures(updatedList);
    }
  };

  return (
    <TestProcedureContext.Provider
      value={{
        procedures,
        addProcedure,
        removeProcedure,
        updateProcedure,
        setProcedures,
      }}
    >
      {children}
    </TestProcedureContext.Provider>
  );
};
